import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { computeTotals, emptyLineItem, formatCurrency } from './lineItems';

// Editable line-item table shared by the quotation and invoice modals. The parent owns
// the items array; this component only reports changes back through onChange.
const LineItemsEditor = ({ items, onChange, discount = 0, onDiscountChange, currency = 'INR', disabled = false }) => {
  const totals = computeTotals(items, discount);

  const updateItem = (index, field, value) => {
    const next = items.map((item, i) => (i === index ? { ...item, [field]: value } : item));
    onChange(next);
  };

  const addItem = () => onChange([...items, emptyLineItem()]);

  // Never let the list empty out - an empty row is friendlier than a zero-item form
  // the server would reject.
  const removeItem = (index) => {
    const next = items.filter((_, i) => i !== index);
    onChange(next.length ? next : [emptyLineItem()]);
  };

  return (
    <div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Qty</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Unit Price</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Tax %</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Amount</th>
              <th className="px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {totals.items.map((item, index) => (
              <tr key={index}>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g. DigiHR Premium - 50 users"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={item.taxPercent}
                    onChange={(e) => updateItem(index, 'taxPercent', e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                </td>
                <td className="px-3 py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">
                  {formatCurrency(item.lineTotal, currency)}
                </td>
                <td className="px-3 py-2 text-center">
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <button
          type="button"
          onClick={addItem}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          <Plus className="w-4 h-4" /> Add line item
        </button>
      )}

      <div className="mt-4 flex justify-end">
        <div className="w-full sm:w-72 space-y-2 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span className="font-medium text-gray-900">{formatCurrency(totals.subtotal, currency)}</span>
          </div>
          <div className="flex justify-between items-center text-gray-600">
            <span>Discount</span>
            {onDiscountChange && !disabled ? (
              <input
                type="number"
                min="0"
                step="0.01"
                value={discount}
                onChange={(e) => onDiscountChange(e.target.value)}
                className="w-28 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span className="font-medium text-gray-900">-{formatCurrency(totals.discountAmount, currency)}</span>
            )}
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Tax</span>
            <span className="font-medium text-gray-900">{formatCurrency(totals.taxTotal, currency)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-gray-200 text-base">
            <span className="font-semibold text-gray-900">Total</span>
            <span className="font-bold text-gray-900">{formatCurrency(totals.total, currency)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineItemsEditor;
